/**
 * Copyright (c) 2026 Lauft App Inc. All rights reserved.
 *
 * This source code is proprietary and confidential.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import AuthService from '../../services/authService';

const API_URL = 'https://back-end-x7tx.onrender.com/api';

const toISODate = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
};

const PRESETS = [
  { label: '7d',  days: 7  },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
];

const PAGE_SIZE = 50;

const AgentMessagesPanel = () => {
  // ── Agent picker ──────────────────────────────────────────────
  const [agents, setAgents]             = useState([]);
  const [agentSearch, setAgentSearch]   = useState('');
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);

  // ── Feed filters ──────────────────────────────────────────────
  const [startDate, setStartDate] = useState(toISODate(daysAgo(90)));
  const [endDate, setEndDate]     = useState(toISODate(new Date()));
  const [userSearch, setUserSearch] = useState('');
  const [contentSearch, setContentSearch] = useState('');
  const [page, setPage]           = useState(1);

  // ── Feed data ─────────────────────────────────────────────────
  const [messages, setMessages]     = useState([]);
  const [pagination, setPagination] = useState(null);
  const [stats, setStats]           = useState(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [expanded, setExpanded]     = useState(null);

  const agentDebounce = useRef(null);
  const feedDebounce  = useRef(null);

  // ── Agents ────────────────────────────────────────────────────
  const fetchAgents = useCallback(async (searchVal) => {
    setAgentsLoading(true);
    try {
      const token = AuthService.getToken();
      const params = new URLSearchParams();
      const s = (searchVal ?? agentSearch).trim();
      if (s) params.set('search', s);

      const res = await fetch(`${API_URL}/messages/admin/agents?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || `Request failed (${res.status})`);
      setAgents(json.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load agents.');
    } finally {
      setAgentsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentSearch]);

  useEffect(() => {
    fetchAgents('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAgentSearch = (val) => {
    setAgentSearch(val);
    if (agentDebounce.current) clearTimeout(agentDebounce.current);
    agentDebounce.current = setTimeout(() => fetchAgents(val), 350);
  };

  // ── Feed ──────────────────────────────────────────────────────
  const fetchFeed = useCallback(async (pageOverride) => {
    if (!selectedAgent) return;
    setLoading(true);
    setError('');

    try {
      const token = AuthService.getToken();
      const params = new URLSearchParams({
        agentId: selectedAgent._id,
        start: startDate,
        end: endDate,
        page: pageOverride ?? page,
        limit: PAGE_SIZE,
      });
      if (userSearch.trim())    params.set('userSearch', userSearch.trim());
      if (contentSearch.trim()) params.set('search', contentSearch.trim());

      const res = await fetch(`${API_URL}/messages/admin/agent-feed?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || `Request failed (${res.status})`);

      setMessages(json.data || []);
      setPagination(json.pagination || null);
      setStats(json.stats || null);
    } catch (err) {
      setError(err.message || 'Failed to load messages.');
    } finally {
      setLoading(false);
    }
  }, [selectedAgent, startDate, endDate, userSearch, contentSearch, page]);

  // Fetch when agent selected or page changes
  useEffect(() => {
    if (selectedAgent) fetchFeed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAgent, page]);

  // Debounced user/content search → reset to page 1
  const handleFeedSearch = (setter) => (val) => {
    setter(val);
    if (feedDebounce.current) clearTimeout(feedDebounce.current);
    feedDebounce.current = setTimeout(() => {
      setPage(1);
      fetchFeed(1);
    }, 400);
  };

  const selectAgent = (agent) => {
    setSelectedAgent(agent);
    setPage(1);
    setMessages([]);
    setStats(null);
    setPagination(null);
    setExpanded(null);
  };

  const applyPreset = (days) => {
    setStartDate(toISODate(daysAgo(days)));
    setEndDate(toISODate(new Date()));
  };

  const handleRefresh = () => {
    setPage(1);
    fetchFeed(1);
  };

  const fmt = (v) => {
    if (loading) return <span className="skeleton">000</span>;
    if (v == null || Number.isNaN(v)) return '—';
    return Number(v).toLocaleString();
  };

  const fmtTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return `${d.toLocaleDateString()} · ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
  };

  const initials = (name) =>
    (name || '?')
      .split(' ')
      .map(w => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase();

  return (
    <div className="admin-panel" style={{ color: '#111', padding: '32px 0 0' }}>
      <style>{`
        /* Agent Messages — matches Control Center design system from UserMetricsPanel */

        .admin-panel .dashboard-card {
          background: #fff;
          border-radius: 14px;
          padding: 20px;
          border: 1px solid #f0f0f0;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
          margin-bottom: 16px;
        }

        .admin-panel .card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
        }

        .admin-panel .card-header h3 {
          margin: 0;
          font-size: 11px;
          font-weight: 600;
          color: #9ca3af;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }

        .admin-panel .alert-danger {
          background-color: rgba(220, 38, 38, 0.08);
          color: #991b1b;
          border: 1px solid rgba(220, 38, 38, 0.25);
          padding: 11px 14px;
          border-radius: 10px;
          margin-bottom: 16px;
          font-size: 13px;
          font-weight: 500;
        }

        .admin-panel .field-label {
          display: block;
          font-size: 10px;
          font-weight: 700;
          color: #9ca3af;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          margin-bottom: 6px;
        }
        .admin-panel .date-input,
        .admin-panel .search-input {
          padding: 8px 10px;
          border: 1px solid #e5e7eb;
          border-radius: 9px;
          font-size: 13px;
          color: #111;
          background: #fff;
          outline: none;
          font-family: inherit;
          min-width: 150px;
        }
        .admin-panel .search-input { min-width: 240px; }
        .admin-panel .date-input:focus,
        .admin-panel .search-input:focus {
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
        }

        .admin-panel .btn {
          padding: 8px 16px;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s;
          font-size: 13px;
          letter-spacing: 0.01em;
          border: 1px solid #e5e7eb;
          background: #fff;
          color: #6b7280;
          font-family: inherit;
        }
        .admin-panel .btn:hover:not(:disabled) { background: #f9fafb; color: #111; }
        .admin-panel .btn:disabled { opacity: 0.6; cursor: default; }
        .admin-panel .btn-primary {
          background: linear-gradient(135deg, #2563eb, #1d4ed8);
          color: #fff;
          border: none;
          box-shadow: 0 2px 8px rgba(37, 99, 235, 0.35);
        }
        .admin-panel .btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #1d4ed8, #1e40af);
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.45);
          color: #fff;
        }

        .admin-panel .preset-chip {
          padding: 6px 12px;
          border-radius: 999px;
          border: 1px solid #e5e7eb;
          background: #fff;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.04em;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.15s;
        }
        .admin-panel .preset-chip:hover { background: #f9fafb; color: #111; }

        /* Agent picker rows */
        .admin-panel .agent-list {
          max-height: 320px;
          overflow-y: auto;
          border: 1px solid #f0f0f0;
          border-radius: 10px;
        }
        .admin-panel .agent-row {
          display: grid;
          grid-template-columns: 36px 1fr 110px 110px;
          gap: 12px;
          align-items: center;
          padding: 10px 14px;
          border-bottom: 1px solid #f5f5f5;
          cursor: pointer;
          transition: background 0.12s;
        }
        .admin-panel .agent-row:last-child { border-bottom: none; }
        .admin-panel .agent-row:hover { background: #f9fafb; }
        .admin-panel .agent-row.selected {
          background: rgba(37, 99, 235, 0.06);
          box-shadow: inset 3px 0 0 #2563eb;
        }
        @media (max-width: 720px) {
          .admin-panel .agent-row { grid-template-columns: 36px 1fr; }
          .admin-panel .agent-row .agent-count { display: none; }
        }
        .admin-panel .agent-avatar {
          width: 32px; height: 32px;
          border-radius: 50%;
          background: #eef2ff;
          color: #1d4ed8;
          font-size: 11px;
          font-weight: 800;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          flex-shrink: 0;
        }
        .admin-panel .agent-avatar img {
          width: 100%; height: 100%; object-fit: cover;
        }
        .admin-panel .agent-name {
          font-size: 13px;
          font-weight: 600;
          color: #111;
          line-height: 1.3;
        }
        .admin-panel .agent-sub {
          font-size: 11px;
          color: #9ca3af;
          font-weight: 500;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .admin-panel .agent-count {
          text-align: right;
          font-size: 13px;
          font-weight: 700;
          color: #111;
          font-variant-numeric: tabular-nums;
        }
        .admin-panel .agent-count .agent-count-label {
          display: block;
          font-size: 9px;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: #9ca3af;
        }

        /* Top summary stat tiles */
        .admin-panel .stat-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
        }
        @media (max-width: 720px) {
          .admin-panel .stat-grid { grid-template-columns: 1fr; }
        }
        .admin-panel .stat-tile {
          background: #fafafa;
          border: 1px solid #f0f0f0;
          border-radius: 12px;
          padding: 18px;
        }
        .admin-panel .stat-tile.primary {
          background: linear-gradient(135deg, rgba(37, 99, 235, 0.06), rgba(29, 78, 216, 0.04));
          border-color: rgba(37, 99, 235, 0.18);
        }
        .admin-panel .stat-label {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #9ca3af;
          margin-bottom: 8px;
        }
        .admin-panel .stat-value {
          font-size: 32px;
          font-weight: 700;
          color: #111;
          letter-spacing: -0.02em;
          line-height: 1.1;
        }
        .admin-panel .stat-tile.primary .stat-value { color: #1d4ed8; }
        .admin-panel .stat-sub {
          font-size: 11px;
          color: #6b7280;
          margin-top: 6px;
        }

        /* Message rows */
        .admin-panel .msg-row {
          display: grid;
          grid-template-columns: 220px 1fr 150px;
          gap: 14px;
          align-items: start;
          padding: 12px 0;
          border-bottom: 1px solid #f5f5f5;
        }
        .admin-panel .msg-row:last-child { border-bottom: none; }
        @media (max-width: 900px) {
          .admin-panel .msg-row { grid-template-columns: 1fr; gap: 6px; }
        }
        .admin-panel .msg-head-row {
          display: grid;
          grid-template-columns: 220px 1fr 150px;
          gap: 14px;
          padding-bottom: 8px;
          border-bottom: 1px solid #f0f0f0;
        }
        @media (max-width: 900px) {
          .admin-panel .msg-head-row { display: none; }
        }
        .admin-panel .msg-head {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #9ca3af;
        }
        .admin-panel .msg-person {
          font-size: 13px;
          font-weight: 600;
          color: #111;
          line-height: 1.3;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .admin-panel .msg-email {
          font-size: 11px;
          color: #9ca3af;
          font-weight: 500;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .admin-panel .msg-content {
          font-size: 13px;
          color: #374151;
          line-height: 1.45;
          cursor: pointer;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }
        .admin-panel .msg-content.expanded {
          display: block;
          -webkit-line-clamp: unset;
          white-space: pre-wrap;
        }
        .admin-panel .msg-time {
          font-size: 12px;
          color: #6b7280;
          font-weight: 500;
          font-variant-numeric: tabular-nums;
          text-align: right;
        }
        @media (max-width: 900px) {
          .admin-panel .msg-time { text-align: left; }
        }

        .admin-panel .empty-state {
          text-align: center;
          padding: 36px 0;
          font-size: 13px;
          color: #9ca3af;
          font-weight: 500;
        }

        /* Pagination */
        .admin-panel .pager {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: 14px;
        }
        .admin-panel .pager-info {
          font-size: 11px;
          color: #9ca3af;
          font-weight: 600;
        }

        .admin-panel .skeleton {
          background: linear-gradient(90deg, #f3f4f6 0%, #fafafa 50%, #f3f4f6 100%);
          background-size: 200% 100%;
          animation: skel 1.4s linear infinite;
          border-radius: 6px;
          color: transparent;
        }
        @keyframes skel { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
      `}</style>

      {/* Branding */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
        <img src="/lauft-logo.png" alt="Lauft" style={{ width: '22px', height: '22px', objectFit: 'contain' }} />
        <span style={{
          fontSize: '11px', fontWeight: '600',
          letterSpacing: '0.08em', color: '#888',
          textTransform: 'uppercase'
        }}>
          Agent Messages
        </span>
      </div>

      {error && <div className="alert-danger">{error}</div>}

      {/* Agent picker */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3>Select Agent</h3>
          {selectedAgent && (
            <span style={{ fontSize: '11px', color: '#1d4ed8', fontWeight: 700 }}>
              Viewing: {selectedAgent.name}
            </span>
          )}
        </div>

        <div style={{ marginBottom: '12px' }}>
          <input
            type="text"
            className="search-input"
            placeholder="Search agents by name, email, or agency…"
            value={agentSearch}
            onChange={(e) => handleAgentSearch(e.target.value)}
            style={{ width: '100%', maxWidth: '420px' }}
          />
        </div>

        <div className="agent-list">
          {agentsLoading && <div className="empty-state">Loading agents…</div>}
          {!agentsLoading && agents.length === 0 && (
            <div className="empty-state">No agents match that search.</div>
          )}
          {!agentsLoading && agents.map(agent => (
            <div
              key={agent._id}
              className={`agent-row ${selectedAgent?._id === agent._id ? 'selected' : ''}`}
              onClick={() => selectAgent(agent)}
            >
              <div className="agent-avatar">
                {agent.profileImage
                  ? <img src={agent.profileImage} alt={agent.name} />
                  : initials(agent.name)}
              </div>
              <div style={{ minWidth: 0 }}>
                <div className="agent-name">{agent.name}</div>
                <div className="agent-sub">
                  {[agent.agency, agent.email].filter(Boolean).join(' · ')}
                </div>
              </div>
              <div className="agent-count">
                <span className="agent-count-label">Conversations</span>
                {agent.conversationCount.toLocaleString()}
              </div>
              <div className="agent-count">
                <span className="agent-count-label">Messages</span>
                {agent.messagesSent.toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedAgent && (
        <>
          {/* Filters */}
          <div className="dashboard-card">
            <div className="card-header">
              <h3>Filters</h3>
              <button
                className="btn btn-primary"
                onClick={handleRefresh}
                disabled={loading}
              >
                {loading ? 'Loading…' : 'Refresh'}
              </button>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-end' }}>
              <div>
                <label className="field-label">Start date</label>
                <input
                  type="date"
                  className="date-input"
                  value={startDate}
                  max={endDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div>
                <label className="field-label">End date</label>
                <input
                  type="date"
                  className="date-input"
                  value={endDate}
                  min={startDate}
                  max={toISODate(new Date())}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <div style={{ display: 'flex', gap: '6px', paddingBottom: '1px' }}>
                {PRESETS.map(p => (
                  <button
                    key={p.label}
                    className="preset-chip"
                    onClick={() => applyPreset(p.days)}
                    type="button"
                  >
                    Last {p.label}
                  </button>
                ))}
              </div>
              <div>
                <label className="field-label">Filter by user</label>
                <input
                  type="text"
                  className="search-input"
                  placeholder="User name or email…"
                  value={userSearch}
                  onChange={(e) => handleFeedSearch(setUserSearch)(e.target.value)}
                />
              </div>
              <div>
                <label className="field-label">Search content</label>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search message text…"
                  value={contentSearch}
                  onChange={(e) => handleFeedSearch(setContentSearch)(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="dashboard-card">
            <div className="card-header">
              <h3>Summary · {selectedAgent.name}</h3>
              <span style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 600 }}>
                {new Date(startDate).toLocaleDateString()} – {new Date(endDate).toLocaleDateString()}
              </span>
            </div>

            <div className="stat-grid">
              <div className="stat-tile primary">
                <div className="stat-label">Messages Sent</div>
                <div className="stat-value">{fmt(stats?.totalMessages)}</div>
                <div className="stat-sub">Matching current filters</div>
              </div>

              <div className="stat-tile">
                <div className="stat-label">Conversations Reached</div>
                <div className="stat-value">{fmt(stats?.distinctConversations)}</div>
                <div className="stat-sub">Distinct user threads in window</div>
              </div>

              <div className="stat-tile">
                <div className="stat-label">Last Message</div>
                <div className="stat-value" style={{ fontSize: '18px', lineHeight: 1.3 }}>
                  {loading ? <span className="skeleton">0000-00-00</span> : fmtTime(stats?.lastSentAt)}
                </div>
                <div className="stat-sub">Most recent send in window</div>
              </div>
            </div>
          </div>

          {/* Message feed */}
          <div className="dashboard-card">
            <div className="card-header">
              <h3>Messages</h3>
              <span style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 600 }}>
                Newest first · click a message to expand
              </span>
            </div>

            <div className="msg-head-row">
              <div className="msg-head">To</div>
              <div className="msg-head">Message</div>
              <div className="msg-head" style={{ textAlign: 'right' }}>Sent</div>
            </div>

            {!loading && messages.length === 0 && (
              <div className="empty-state">No messages match the current filters.</div>
            )}

            {messages.map(m => (
              <div key={m._id} className="msg-row">
                <div>
                  <div className="msg-person">{m.recipient?.name || '—'}</div>
                  <div className="msg-email">{m.recipient?.email || ''}</div>
                </div>

                <div
                  className={`msg-content ${expanded === m._id ? 'expanded' : ''}`}
                  onClick={() => setExpanded(expanded === m._id ? null : m._id)}
                  title={expanded === m._id ? 'Click to collapse' : 'Click to expand'}
                >
                  {m.content}
                </div>

                <div className="msg-time">{fmtTime(m.createdAt)}</div>
              </div>
            ))}

            {pagination && pagination.totalPages > 1 && (
              <div className="pager">
                <button
                  className="btn"
                  disabled={loading || page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                >
                  ← Prev
                </button>
                <span className="pager-info">
                  Page {pagination.page} of {pagination.totalPages} · {pagination.total.toLocaleString()} messages
                </span>
                <button
                  className="btn"
                  disabled={loading || page >= pagination.totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {!selectedAgent && (
        <div className="dashboard-card">
          <div className="empty-state">Select an agent above to view their message history.</div>
        </div>
      )}
    </div>
  );
};

export default AgentMessagesPanel;